import {
  Box,
  Button,
  Checkbox,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { SelectChangeEvent } from "@mui/material/Select";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useApiUrl } from "@refinedev/core";
import { Edit } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import userRole from "@warriortrading/warriortrading.hermes.common.feathers/enums/userRole";
import { ReadableText } from "components/readableText";
import { useCallback, useEffect, useState } from "react";
import { dataProvider } from "rest-data-provider";
import { config } from "../../config";
import AddBadges from "./addBadges";
import AddProductRoom from "./addProductRoom";
import AddQuotestreamPackage from "./addQuotestreamPackage";

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 600, // Double the height to show more content
    },
  },
};

const All = "All";

const SYMBOL_SEARCH = "Symbol_Search";

const SYMBOL_CHARTING = "Symbol_Charting";

const DefaultSimPrivilegesArr = [
  { value: "T_Level2", type: "Level 2 & Order Entry" },
  { value: "T_Accounts", type: "Accounts" },
  { value: "T_Positions", type: "Positions" },
  { value: "T_Monitors", type: "Trade Activity" },
  { value: "Metrics", type: "Metrics" },
  { value: "S_Announcements", type: "Announcements" },
];

export const ProdCategoriesEdit = () => {
  const {
    saveButtonProps,
    refineCore: { queryResult },
    register,
    setValue: setFormValue,
    formState: { errors },
  } = useForm();
  const stockUrl = config().server.stock;
  const data = queryResult?.data?.data;
  const [value, setValue] = useState<string[]>([]);
  const handleChange = (event: SelectChangeEvent<typeof value>) => {
    const res =
      typeof event.target.value === "string"
        ? event.target.value.split(",")
        : event.target.value;

    if (res.includes(All)) {
      if (isSelectAll()) {
        setValue([]);
      } else {
        setValue(privilegesArr.map((e) => e.value));
      }
    } else {
      const updated = res.filter((e) => e !== All && !!e) as string[];
      setValue(updated);
      // sync with react-hook-form so save sends updated privileges to backend
      try {
        setFormValue("privileges", updated.join(","));
      } catch (e) {}

      // If Symbol_Charting is selected in privileges, ensure Symbol_Search is selected
      if (
        updated.includes(SYMBOL_CHARTING) &&
        privilegesArr.some((p) => p.value === SYMBOL_SEARCH) &&
        !updated.includes(SYMBOL_SEARCH)
      ) {
        const newVals = Array.from(
          new Set([...(updated || []), SYMBOL_SEARCH])
        );
        setValue(newVals);
        try {
          setFormValue("privileges", newVals.join(","));
        } catch (e) {}
      }
    }
  };
  const [simPrivilegesValue, setSimPrivilegesValue] = useState<string[]>([]);
  const handleSimPrivilegesChange = (
    event: SelectChangeEvent<typeof simPrivilegesValue>
  ) => {
    const res =
      typeof event.target.value === "string"
        ? event.target.value.split(",")
        : event.target.value;

    if (res.includes(All)) {
      if (isSimPrivilegesSelectAll()) {
        setSimPrivilegesValue([]);
      } else {
        setSimPrivilegesValue(simPrivilegesArr.map((e) => e.value));
      }
    } else {
      const updated = res.filter((e) => e !== All && !!e) as string[];
      setSimPrivilegesValue(updated);

      // If T_Level2 is selected in sim privileges, ensure Symbol_Search is selected in chatroom privileges
      if (
        updated.includes("T_Level2") &&
        privilegesArr.some((p) => p.value === SYMBOL_SEARCH) &&
        !value.includes(SYMBOL_SEARCH)
      ) {
        const newVals = Array.from(new Set([...(value || []), SYMBOL_SEARCH]));
        setValue(newVals);
        try {
          setFormValue("privileges", newVals.join(","));
        } catch (e) {}
      }
    }
  };
  type privilegesArrType = Array<{ value: string; type: string }>;
  const [privilegesArr, setPrivilegesArr] = useState<privilegesArrType>([]);
  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [isWarriorPro, setIsWarriorPro] = useState(false);
  const [simAccess, setSimAccess] = useState(false);
  const [simBetaAccess, setSimBetaAccess] = useState(false);
  type simPrivilegesArrType = Array<{ value: string; type: string }>;
  const [simPrivilegesArr, setSimPrivilegesArr] =
    useState<simPrivilegesArrType>(DefaultSimPrivilegesArr);

  const isSelectAll = (): boolean => {
    return value.length === privilegesArr.length;
  };
  const isSimPrivilegesSelectAll = (): boolean => {
    return simPrivilegesValue.length === simPrivilegesArr.length;
  };

  useEffect(() => {
    if (data) {
      const privileges = data?.privileges;
      setValue(privileges ? privileges.split(",") : []);
      const simPrivileges = data?.simPrivileges;
      setSimPrivilegesValue(simPrivileges ? simPrivileges.split(",") : []);
    }
    if (data?.createdAt) {
      setCreatedAt(new Date(data?.createdAt).toLocaleString());
    }
    if (data?.updatedAt) {
      setUpdatedAt(new Date(data?.updatedAt).toLocaleString());
    }
    setIsWarriorPro(data?.isWarriorPro);
    setSimAccess(data?.simAccess);
    setSimBetaAccess(data?.simBetaAccess);
  }, [data]);

  const transformWidgets = (widgets: any) => {
    // check is array
    if (!widgets || !Array.isArray(widgets)) {
      return [];
    }

    return widgets.map((widget: any) => ({
      value: widget.id,
      type: widget.name,
    }));
  };
  const privilegesData = useCallback(() => {
    return dataProvider(stockUrl)
      .getList({ resource: "v1/scanner/config" })
      .then((res) => {
        if (res) {
          const data = res?.data as any;
          const privilegesList = [
            ...transformWidgets(data?.toplist?.widgets),
            ...transformWidgets(data?.alert?.widgets),
            ...transformWidgets(data?.others?.widgets),
          ];
          // Sort privileges alphabetically by type (name)
          const sortedPrivilegesList = privilegesList.sort((a, b) =>
            a.type.localeCompare(b.type)
          );
          setPrivilegesArr(sortedPrivilegesList);
        }
        return res.data;
      });
  }, [stockUrl]);
  useEffect(() => {
    privilegesData();
  }, [privilegesData]);

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("crmId", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.crmId}
          helperText={(errors as any)?.crmId?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="CrmProduct"
          name="crmId"
        />
        <FormControl
          fullWidth
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          <InputLabel id="privileges">Scanner Privileges</InputLabel>
          <Select
            {...register("privileges", {})}
            multiple
            renderValue={(value) => (
              <ReadableText text={value.filter((i) => !!i).join(", ")} />
            )}
            labelId="privileges"
            value={value}
            label="Scanner Privileges"
            onChange={handleChange}
            MenuProps={MenuProps}
          >
            <MenuItem key={All} value={All}>
              <Checkbox checked={isSelectAll()} />
              {All}
            </MenuItem>
            {privilegesArr.map((item: any) => {
              return (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={value.includes(item.value)} />
                  {item.type}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          <InputLabel id="simPrivileges">Sim Privileges</InputLabel>
          <Select
            {...register("simPrivileges", {})}
            multiple
            renderValue={(value) => (
              <ReadableText text={value.filter((i) => !!i).join(", ")} />
            )}
            labelId="simPrivileges"
            value={simPrivilegesValue}
            label="Sim Privileges"
            onChange={handleSimPrivilegesChange}
            MenuProps={MenuProps}
          >
            <MenuItem key={All} value={All}>
              <Checkbox checked={isSimPrivilegesSelectAll()} />
              {All}
            </MenuItem>
            {simPrivilegesArr.map((item: any) => {
              return (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={simPrivilegesValue.includes(item.value)} />
                  {item.type}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <TextField
          {...register("name", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />
        <TextField
          {...register("description", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.description}
          helperText={(errors as any)?.description?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Description"
          name="description"
        />
        <label>
          Is Warrior Pro
          <Switch
            {...register("isWarriorPro", {})}
            checked={isWarriorPro}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setIsWarriorPro(event.target.checked as any);
            }}
          />
        </label>
        <label>
          Sim Access
          <Switch
            {...register("simAccess", {})}
            checked={simAccess}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSimAccess(event.target.checked as any);
            }}
          />
        </label>
        <label>
          Sim Beta Access
          <Switch
            {...register("simBetaAccess", {})}
            checked={simBetaAccess}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSimBetaAccess(event.target.checked as any);
            }}
          />
        </label>
        <TextField
          {...register("createdAt", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.createdAt}
          helperText={(errors as any)?.createdAt?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="CreatedAt"
          name="createdAt"
          value={createdAt}
          disabled
        />
        <TextField
          {...register("updatedAt", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.updatedAt}
          helperText={(errors as any)?.updatedAt?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="UpdatedAt"
          name="updatedAt"
          value={updatedAt}
          disabled
        />
      </Box>
    </Edit>
  );
};

export const ProdCategoriesEdit2 = () => {
  const {
    refineCore: { queryResult },
  } = useForm();
  const columns: GridColDef[] = [
    { field: "room.title", headerName: "RoomTitle", minWidth: 120, flex: 1 },
    {
      field: "attendee",
      headerName: "Attendee",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return userRole.fromValue(row.role)?.name;
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return (
          <>
            <Button variant="contained" onClick={() => removeRoom(row.id)}>
              REMOVE
            </Button>
          </>
        );
      },
    },
  ];
  const [rows, setRow] = useState([]);
  const [id, setId] = useState("");
  const apiUrl = useApiUrl();
  const fetchData = useCallback(
    (id: any) => {
      return dataProvider(apiUrl)
        .getList({
          resource: "prod-category-room-relations",
          filters: [
            {
              field: "categoryId",
              operator: "eq",
              value: id,
            },
          ],
        })
        .then((res) => {
          if (res?.data) {
            const data = res?.data as any;
            setRow(data);
          }
          return res?.data;
        });
    },
    [apiUrl]
  );

  useEffect(() => {
    if (queryResult?.data?.data?.id) {
      setId(queryResult?.data?.data?.id + "");
      fetchData(queryResult?.data?.data?.id + "");
    }
  }, [queryResult?.data?.data.id, id, fetchData]);

  const removeRoom = async (rowId: any) => {
    await dataProvider(apiUrl).deleteOne({
      resource: "prod-category-room-relations",
      id: rowId,
    });
    fetchData(id);
  };
  const [open, setOpen] = useState(false);
  const addProductRoom = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Edit
      saveButtonProps={{ style: { display: "none" } }}
      headerProps={{
        style: {
          contentVisibility: "hidden",
        },
      }}
      breadcrumb={
        <div
          style={{
            display: "none",
          }}
        ></div>
      }
    >
      {open ? (
        <AddProductRoom
          isOpen={true}
          id={id}
          handleClose={handleClose}
          fetchRoomsData={fetchData}
        />
      ) : null}
      <h3 style={{ marginLeft: "5px" }}>
        Rooms Bound to This Product Category
      </h3>
      <div
        style={{ display: "flex", justifyContent: "end", marginBottom: "10px" }}
      >
        <Button variant="contained" onClick={addProductRoom}>
          +
        </Button>
      </div>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight
        />
      </Box>
    </Edit>
  );
};

export const ProdCategoriesEdit3 = () => {
  const {
    refineCore: { queryResult },
  } = useForm();
  const columns: GridColDef[] = [
    {
      field: "badge.badgeUrl",
      headerName: "BadgeUrl",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ value }) {
        return (
          <>
            <img alt="imgUrl" src={value} width={45} />
          </>
        );
      },
    },
    { field: "badge.code", headerName: "Code", minWidth: 120, flex: 1 },
    { field: "badge.name", headerName: "Name", minWidth: 120, flex: 1 },
    {
      field: "badge.expirationDate",
      headerName: "ExpirationDate",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return (
          <>
            <Button variant="contained" onClick={() => removeBadge(row.id)}>
              REMOVE
            </Button>
          </>
        );
      },
    },
  ];
  const [rows, setRow] = useState([]);
  const [id, setId] = useState("");
  const apiUrl = useApiUrl();
  const fetchData = useCallback(
    (id: any) => {
      return dataProvider(apiUrl)
        .getList({
          resource: "prod-category-badge-relations",
          filters: [
            {
              field: "categoryId",
              operator: "eq",
              value: id,
            },
          ],
        })
        .then((res) => {
          if (res?.data) {
            const data = res?.data as any;
            setRow(data);
          }
          return res?.data;
        });
    },
    [apiUrl]
  );
  useEffect(() => {
    if (queryResult?.data?.data?.id) {
      setId(queryResult?.data?.data?.id + "");
      fetchData(queryResult?.data?.data?.id + "");
    }
  }, [queryResult?.data?.data.id, id, fetchData]);

  const removeBadge = async (rowId: any) => {
    await dataProvider(apiUrl).deleteOne({
      resource: "prod-category-badge-relations",
      id: rowId,
    });
    fetchData(id);
  };
  const [open, setOpen] = useState(false);
  const addBadge = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Edit
      saveButtonProps={{ style: { display: "none" } }}
      headerProps={{
        style: {
          contentVisibility: "hidden",
        },
      }}
      breadcrumb={
        <div
          style={{
            display: "none",
          }}
        ></div>
      }
    >
      {open ? (
        <AddBadges
          isOpen={true}
          id={id}
          handleClose={handleClose}
          rows={rows}
          fetchBadgeData={fetchData}
        />
      ) : null}
      <h3 style={{ marginLeft: "5px" }}>
        Badges Bound to This Product Category
      </h3>
      <div
        style={{ display: "flex", justifyContent: "end", marginBottom: "10px" }}
      >
        <Button variant="contained" onClick={addBadge}>
          +
        </Button>
      </div>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight
        />
      </Box>
    </Edit>
  );
};

export const ProdCategoriesEditPackage = () => {
  const {
    refineCore: { queryResult },
  } = useForm();
  const columns: GridColDef[] = [
    {
      field: "quotestreamPackage.name",
      headerName: "Name",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "quotestreamPackage.description",
      headerName: "Description",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "quotestreamPackage.status",
      headerName: "status",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return (
          <>{row["quotestreamPackage.status"] == 0 ? "ENABLE" : "DISABLE"}</>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      flex: 1,
      renderCell: function render({ row }) {
        return (
          <>
            <Button variant="contained" onClick={() => removeBadge(row.id)}>
              REMOVE
            </Button>
          </>
        );
      },
    },
  ];
  const [rows, setRow] = useState([]);
  const [id, setId] = useState("");
  const apiUrl = useApiUrl();
  const fetchData = useCallback(
    (id: any) => {
      return dataProvider(apiUrl)
        .getList({
          resource: "prod-category-quotestream-package-relations",
          filters: [
            {
              field: "categoryId",
              operator: "eq",
              value: id,
            },
          ],
        })
        .then((res) => {
          if (res?.data) {
            const data = res?.data as any;
            setRow(data);
          }
          return res?.data;
        });
    },
    [apiUrl]
  );
  useEffect(() => {
    if (queryResult?.data?.data?.id) {
      setId(queryResult?.data?.data?.id + "");
      fetchData(queryResult?.data?.data?.id + "");
    }
  }, [queryResult?.data?.data.id, id, fetchData]);

  const removeBadge = async (rowId: any) => {
    await dataProvider(apiUrl).deleteOne({
      resource: "prod-category-quotestream-package-relations",
      id: rowId,
    });
    fetchData(id);
  };
  const [open, setOpen] = useState(false);
  const addBadge = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Edit
      saveButtonProps={{ style: { display: "none" } }}
      headerProps={{
        style: {
          contentVisibility: "hidden",
        },
      }}
      breadcrumb={
        <div
          style={{
            display: "none",
          }}
        ></div>
      }
    >
      {open ? (
        <AddQuotestreamPackage
          isOpen={true}
          id={id}
          handleClose={handleClose}
          rows={rows}
          fetchBadgeData={fetchData}
        />
      ) : null}
      <h3 style={{ marginLeft: "5px" }}>
        Quotestream Package Bound to This Product Category
      </h3>
      <div
        style={{ display: "flex", justifyContent: "end", marginBottom: "10px" }}
      >
        <Button variant="contained" onClick={addBadge}>
          +
        </Button>
      </div>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight
        />
      </Box>
    </Edit>
  );
};
